/**
 * Pure logic for WorldScene's modal-overlay tracking. Owns the Set
 * transition that PR 3a generalizes from the prior `partySheetOpen`
 * boolean — additional sources (settings/bag/pokedex/pokedexFullSheet)
 * become Set members in PR 3b without touching this module.
 *
 * The "transition" output is the single load-bearing predicate
 * WorldScene reads to decide whether the orb-dim / dialogue-suppress /
 * joystick-suppress side-effect chain needs to fire: it's true only
 * when `isOpen` flipped, so successive `setModalOverlayOpen(true,
 * source)` calls for an already-tracked source are idempotent (no
 * double-fire) and removing a source while another is still open is a
 * no-op (the chain stays armed). Mirrors the pre-PR-3a
 * setPartySheetOpen `if (this.partySheetOpen === open) return` early
 * exit, generalized over the Set.
 *
 * vitest-only (no Phaser import) per the dialogueOverlayLogic /
 * snapshotBackdropLogic precedent.
 */

export type ModalOverlaySource =
  | 'partySheet'
  | 'settings'
  | 'bag'
  | 'pokedex'
  | 'pokedexFullSheet';

export interface ModalOverlayTransition {
  /** Snapshot of the source set after applying the open/close op.
   *  WorldScene replaces its field with this. */
  readonly sources: ReadonlySet<ModalOverlaySource>;
  /** True iff `sources.size > 0` after the op. */
  readonly isOpen: boolean;
  /** True iff `isOpen` flipped vs. the prior state. The side-effect
   *  chain (orbs dim, dialogue suppress, joystick suppress) fires only
   *  on a transition — successive opens of the same source, or closes
   *  that leave the set non-empty, are no-ops. */
  readonly transitioned: boolean;
}

/**
 * Apply an open/close op to the source set. Returns a fresh ReadonlySet
 * (immutable-style — easy to reason about in tests) plus the
 * transition diagnostic the WorldScene consumer needs.
 */
export function applyModalOverlay(
  current: ReadonlySet<ModalOverlaySource>,
  open: boolean,
  source: ModalOverlaySource,
): ModalOverlayTransition {
  const wasOpen = current.size > 0;
  const next = new Set(current);
  if (open) next.add(source);
  else next.delete(source);
  const isOpen = next.size > 0;
  return {
    sources: next,
    isOpen,
    transitioned: wasOpen !== isOpen,
  };
}
